<template>
  <section class="manager-section">
    <div v-if="view === 'list'">
      <header class="manager-page-header">
        <div>
          <h1>Properties</h1>
          <p>Winelands property records</p>
        </div>
        <button class="manager-button manager-button-primary" type="button" @click="startCreate">
          Add property
        </button>
      </header>

      <div class="manager-toolbar">
        <input
          v-model.trim="search"
          class="manager-toolbar-search"
          type="search"
          placeholder="Search by PALE reference, property or location"
          aria-label="Search properties"
        >
        <select
          v-model="statusFilter"
          class="manager-toolbar-filter"
          aria-label="Filter properties by publication status"
        >
          <option value="Active">Published</option>
          <option value="Draft">Drafts</option>
          <option value="Pending">Pending</option>
          <option value="Archived">Archived</option>
          <option value="all">All statuses</option>
        </select>
      </div>

      <div v-if="message" class="manager-notice manager-notice-success">{{ message }}</div>
      <div v-if="warning" class="manager-notice manager-notice-warning">{{ warning }}</div>
      <div v-if="error" class="manager-notice manager-notice-error">{{ error }}</div>

      <div class="manager-table-wrap">
        <table class="manager-table">
          <thead>
            <tr>
              <th>Web reference</th>
              <th>Property</th>
              <th>Location</th>
              <th>Price</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in filteredRows" :key="row.privateRefId || row.webRef">
              <td><strong>{{ row.webRef }}</strong></td>
              <td>
                <strong>{{ row.heading || 'Untitled property' }}</strong>
                <span>{{ row.type }}</span>
              </td>
              <td>{{ row.location || '—' }}</td>
              <td>{{ formatPrice(row.price) }}</td>
              <td><span class="manager-status">{{ row.status }}</span></td>
              <td class="manager-table-action">
                <button class="manager-button manager-button-secondary" type="button" @click="startEdit(row)">
                  Edit
                </button>
              </td>
            </tr>
            <tr v-if="!loading && !filteredRows.length">
              <td colspan="6" class="manager-empty">No properties found.</td>
            </tr>
            <tr v-if="loading">
              <td colspan="6" class="manager-empty">Loading properties…</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-else>
      <header class="manager-page-header">
        <div>
          <button class="manager-back" type="button" @click="returnToList">← Properties</button>
          <h1>{{ isEditing ? 'Edit property' : 'Add property' }}</h1>
          <p>{{ webRefPreview || 'New Winelands property' }}</p>
        </div>
        <div class="manager-header-actions">
          <button v-if="isEditing" class="manager-button manager-button-danger" type="button" @click="removeProperty" :disabled="saving">
            Delete
          </button>
          <button class="manager-button manager-button-primary" type="button" @click="saveProperty" :disabled="saving || uploadingImages">
            {{ saving ? 'Saving…' : 'Save property' }}
          </button>
        </div>
      </header>

      <div v-if="warning" class="manager-notice manager-notice-warning">{{ warning }}</div>
      <div v-if="error" class="manager-notice manager-notice-error">{{ error }}</div>
      <div v-if="message" class="manager-notice manager-notice-success">{{ message }}</div>

      <nav class="manager-editor-tabs" aria-label="Property editor sections">
        <button
          v-for="tab in editorTabs"
          :key="tab.key"
          type="button"
          :class="{ active: activeTab === tab.key }"
          @click="activeTab = tab.key"
        >
          {{ tab.label }}
        </button>
      </nav>

      <form class="manager-form" @submit.prevent="saveProperty">
        <section v-show="activeTab === 'overview'" class="manager-form-section">
          <h2>Property details</h2>
          <div class="manager-form-grid">
            <label>
              <span>Web reference</span>
              <div class="manager-prefix-input">
                <strong>PALE-</strong>
                <input
                  v-model="form.webNumber"
                  inputmode="numeric"
                  pattern="[0-9]+"
                  placeholder="001"
                  required
                >
              </div>
            </label>
            <label>
              <span>Property category</span>
              <select v-model="form.managerType" required @change="handleManagerTypeChange">
                <option value="residential_sale">Residential sale</option>
                <option value="residential_rental">Residential rental</option>
                <option value="commercial_sale">Commercial sale</option>
                <option value="commercial_rental">Commercial rental</option>
              </select>
            </label>
            <label>
              <span>Property type</span>
              <select v-model="form.propertyType" required>
                <option
                  v-if="form.propertyType && propertyTypeOptions.indexOf(form.propertyType) === -1"
                  :value="form.propertyType"
                >{{ form.propertyType }}</option>
                <option v-for="type in propertyTypeOptions" :key="type" :value="type">{{ type }}</option>
              </select>
            </label>
            <label>
              <span>Status</span>
              <select v-model="form.status">
                <option>Draft</option>
                <option>Active</option>
                <option>Pending</option>
                <option>Archived</option>
              </select>
            </label>
            <label>
              <span>Public property banner</span>
              <select v-model="form.saleStatus">
                <option value="">Off</option>
                <option value="For Sale">For Sale</option>
                <option value="Under Offer">Under Offer</option>
                <option value="Sold">Sold</option>
              </select>
            </label>
            <label class="manager-field-wide">
              <span>Marketing heading</span>
              <input v-model.trim="form.heading" required>
            </label>
            <label class="manager-field-wide">
              <span>Description</span>
              <textarea v-model="form.description" rows="7"></textarea>
            </label>
          </div>
        </section>

        <section v-show="activeTab === 'overview'" class="manager-form-section">
          <h2>Price and location</h2>
          <div class="manager-form-grid manager-form-grid-four">
            <label>
              <span>Price</span>
              <input v-model.number="form.price" type="number" min="0">
            </label>
            <label>
              <span>Price term</span>
              <select v-model="form.priceTerm">
                <option
                  v-if="form.priceTerm && priceTermOptions.indexOf(form.priceTerm) === -1"
                  :value="form.priceTerm"
                >{{ form.priceTerm }}</option>
                <option value="">Not applicable</option>
                <option v-for="term in priceTermOptions" :key="term" :value="term">{{ term }}</option>
              </select>
            </label>
            <label>
              <span>Suburb</span>
              <input v-model.trim="form.suburb">
            </label>
            <label>
              <span>Region</span>
              <input v-model.trim="form.region">
            </label>
            <label>
              <span>Latitude</span>
              <input v-model.trim="form.latitude">
            </label>
            <label>
              <span>Longitude</span>
              <input v-model.trim="form.longitude">
            </label>
            <label class="manager-checkbox">
              <input v-model="form.poa" type="checkbox">
              <span>Price on application</span>
            </label>
            <label class="manager-checkbox">
              <input v-model="form.onShow" type="checkbox">
              <span>On show</span>
            </label>
          </div>
        </section>

        <section v-show="activeTab === 'features'" class="manager-form-section">
          <h2>Property features</h2>
          <div class="manager-form-grid manager-property-count-grid">
            <label><span>Bedrooms</span><input v-model.number="form.bedrooms" type="number" min="0" placeholder="0"></label>
            <label><span>Bathrooms</span><input v-model.number="form.bathrooms" type="number" min="0" step="0.5" placeholder="0"></label>
            <label><span>Lounges</span><input v-model.number="form.lounges" type="number" min="0" placeholder="0"></label>
            <label><span>Garages</span><input v-model.number="form.garages" type="number" min="0" placeholder="0"></label>
            <label><span>Parking</span><input v-model.number="form.parking" type="number" min="0" placeholder="0"></label>
          </div>

          <div class="manager-size-pairs">
            <div class="manager-size-pair">
              <label>
                <span>Land size</span>
                <input
                  v-model.number="form.landSize"
                  type="number"
                  min="0"
                  step="any"
                  placeholder="Enter land size"
                >
              </label>
              <label>
                <span>Land measurement type</span>
                <select v-model="form.landMeasurement" aria-label="Land measurement type">
                  <option
                    v-if="form.landMeasurement && sizeUnitValues.indexOf(form.landMeasurement) === -1"
                    :value="form.landMeasurement"
                  >{{ form.landMeasurement }}</option>
                  <option v-for="unit in sizeUnitOptions" :key="'land-' + unit.value" :value="unit.value">
                    {{ unit.label }}
                  </option>
                </select>
              </label>
            </div>

            <div class="manager-size-pair">
              <label>
                <span>Building size</span>
                <input
                  v-model.number="form.buildingSize"
                  type="number"
                  min="0"
                  step="any"
                  placeholder="Enter building size"
                >
              </label>
              <label>
                <span>Building measurement type</span>
                <select v-model="form.buildingMeasurement" aria-label="Building measurement type">
                  <option
                    v-if="form.buildingMeasurement && sizeUnitValues.indexOf(form.buildingMeasurement) === -1"
                    :value="form.buildingMeasurement"
                  >{{ form.buildingMeasurement }}</option>
                  <option v-for="unit in sizeUnitOptions" :key="'building-' + unit.value" :value="unit.value">
                    {{ unit.label }}
                  </option>
                </select>
              </label>
            </div>
          </div>
        </section>

        <section v-show="activeTab === 'media'" class="manager-form-section">
          <h2>Media and agents</h2>
          <div class="manager-form-grid">
            <div class="manager-field-wide manager-image-manager">
              <div class="manager-image-heading">
                <div>
                  <strong>Property images</strong>
                  <small>Images are resized and compressed when uploaded.</small>
                </div>
                <span v-if="internalId" class="manager-folder-label">Saved property</span>
              </div>

              <label
                class="manager-upload-dropzone"
                :class="{ disabled: uploadingImages || !internalId, locked: !internalId }"
              >
                <input
                  ref="imageInput"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  :disabled="uploadingImages || !internalId"
                  @change="uploadSelectedImages"
                >
                <strong>{{ !internalId ? 'Save property before uploading images' : (uploadingImages ? 'Uploading and compressing…' : 'Choose property images') }}</strong>
                <span>{{ !internalId ? 'The gallery becomes available after the property has a database ID.' : 'JPEG, PNG or WebP. You can select several images at once.' }}</span>
              </label>

              <div v-if="uploadingImages" class="manager-upload-progress">
                <span :style="{ width: uploadProgress + '%' }"></span>
              </div>

              <div v-if="form.images.length" class="manager-property-images">
                <article v-for="(image, index) in form.images" :key="image + '-' + index" class="manager-property-image">
                  <img :src="image" :alt="webRefPreview + ' image ' + (index + 1)">
                  <span v-if="image === form.featuredImage" class="manager-cover-badge">Featured image</span>
                  <div class="manager-image-actions">
                    <button v-if="image !== form.featuredImage" type="button" @click="setFeaturedImage(image)">Set as featured</button>
                    <button type="button" class="danger" @click="removeImage(index)" :disabled="uploadingImages">Remove</button>
                  </div>
                </article>
              </div>

              <p v-else class="manager-image-empty">No property images uploaded yet.</p>
            </div>
            <label>
              <span>YouTube URL or video ID</span>
              <input
                v-model.trim="form.videoId"
                type="text"
                placeholder="https://www.youtube.com/watch?v=..."
              >
              <small>Paste a full YouTube link or its video ID. Leave blank to hide the video.</small>
            </label>
            <label>
              <span>Primary agent</span>
              <select v-model="form.agent">
                <option value="">No agent</option>
                <option v-for="agent in agentOptions" :key="agent.value" :value="agent.value">{{ agent.label }}</option>
              </select>
            </label>
            <label>
              <span>Second agent</span>
              <select v-model="form.agent2">
                <option value="">No agent</option>
                <option v-for="agent in agentOptions" :key="agent.value" :value="agent.value">{{ agent.label }}</option>
              </select>
            </label>
            <label>
              <span>Third agent</span>
              <select v-model="form.agent3">
                <option value="">No agent</option>
                <option v-for="agent in agentOptions" :key="agent.value" :value="agent.value">{{ agent.label }}</option>
              </select>
            </label>
          </div>
        </section>

        <section v-show="activeTab === 'publishing'" class="manager-form-section">
          <h2>Publishing</h2>
          <div class="manager-form-grid">
            <label class="manager-checkbox">
              <input v-model="form.featured" type="checkbox">
              <span>Featured property</span>
            </label>
            <label class="manager-field-wide">
              <span>Card summary</span>
              <textarea v-model="form.summary" rows="3"></textarea>
            </label>
            <label class="manager-field-wide">
              <span>SEO title</span>
              <input v-model.trim="form.seoTitle">
            </label>
            <label class="manager-field-wide">
              <span>SEO description</span>
              <textarea v-model="form.seoDescription" rows="3"></textarea>
            </label>
          </div>
        </section>

        <div class="manager-form-footer">
          <button class="manager-button manager-button-secondary" type="button" @click="returnToList">Cancel</button>
          <button class="manager-button manager-button-primary" type="submit" :disabled="saving || uploadingImages">
            {{ saving ? 'Saving…' : 'Save property' }}
          </button>
        </div>
      </form>
    </div>
  </section>
</template>

<script>
import {
  apiErrorMessage,
  canonicalWebRef,
  createManagedRecord,
  deleteManagedRecord,
  deletePropertyImage,
  getAgentData,
  getPropertyListing,
  getRecordPayload,
  listManagedRecords,
  makeWebRef,
  nextWebRef,
  normaliseInternalId,
  normaliseYouTubeVideoId,
  uploadPropertyImages,
  updateManagedRecord,
  webRefNumber
} from '@/services/winelandsApi'

function normalisePriceTerm(value) {
  const raw = value === null || value === undefined ? '' : String(value).trim()
  const knownTerms = ['per month', 'per week', 'per day', 'per annum']
  const matched = knownTerms.find(function (term) {
    return term === raw.toLowerCase()
  })
  return matched || raw
}

function emptyForm() {
  return {
    webNumber: '',
    managerType: 'residential_sale',
    propertyType: 'House',
    status: 'Draft',
    saleStatus: '',
    heading: '',
    description: '',
    price: 0,
    priceTerm: '',
    suburb: '',
    region: '',
    latitude: '',
    longitude: '',
    poa: false,
    onShow: false,
    bedrooms: 0,
    bathrooms: 0,
    lounges: 0,
    garages: 0,
    parking: 0,
    landSize: '',
    landMeasurement: 'm²',
    buildingSize: '',
    buildingMeasurement: 'm²',
    images: [],
    featuredImage: '',
    videoId: '',
    agent: '',
    agent2: '',
    agent3: '',
    featured: false,
    summary: '',
    seoTitle: '',
    seoDescription: ''
  }
}

export default {
  name: 'PropertyManager',

  data: function () {
    return {
      records: [],
      agents: [],
      view: 'list',
      activeTab: 'overview',
      privateRefId: '',
      internalId: null,
      form: emptyForm(),
      search: '',
      statusFilter: 'Active',
      loading: false,
      saving: false,
      uploadingImages: false,
      uploadProgress: 0,
      error: '',
      warning: '',
      message: '',
      savedFormState: ''
    }
  },

  computed: {
    isEditing: function () {
      return Boolean(this.privateRefId)
    },

    isDirty: function () {
      return this.view === 'editor' && this.savedFormState !== this.formState()
    },

    webRefPreview: function () {
      return makeWebRef(this.form.webNumber)
    },

    priceTermOptions: function () {
      return this.form.managerType.indexOf('rental') !== -1
        ? ['per month', 'per week', 'per day', 'per annum']
        : []
    },

    sizeUnitOptions: function () {
      return [
        { value: 'm²', label: 'Square metres (m²)' },
        { value: 'ha', label: 'Hectares (ha)' },
        { value: 'acres', label: 'Acres' },
        { value: 'ft²', label: 'Square feet (ft²)' },
        { value: 'm³', label: 'Cubic metres (m³)' }
      ]
    },

    sizeUnitValues: function () {
      return this.sizeUnitOptions.map(function (unit) { return unit.value })
    },

    editorTabs: function () {
      return [
        { key: 'overview', label: 'Overview' },
        { key: 'features', label: 'Features' },
        { key: 'media', label: 'Media & agents' },
        { key: 'publishing', label: 'Publishing' }
      ]
    },

    propertyTypeOptions: function () {
      if (this.form.managerType.indexOf('commercial') === 0) {
        return [
          'Commercial Property', 'Office', 'Retail', 'Industrial', 'Warehouse',
          'Vacant Land', 'Farm', 'Small Holding', 'Guest House'
        ]
      }

      return [
        'House', 'Apartment', 'Townhouse', 'Vacant Land', 'Farm',
        'Small Holding', 'Lodge', 'Estate', 'Guest House'
      ]
    },

    rows: function () {
      return this.records.map(function (record) {
        const payload = getRecordPayload(record)
        const listing = getPropertyListing(record)
        return {
          record: record,
          privateRefId: record.ref_id,
          internalId: normaliseInternalId(record.id),
          webRef: canonicalWebRef(listing.web_ref || listing.own_ref_number || record.ref_id),
          heading: listing.marketing_heading,
          type: listing.property_type || payload.manager_type,
          location: [listing.loc_suburb, listing.loc_region].filter(Boolean).join(', '),
          price: Number(listing.price || 0),
          status: listing.status || 'Active'
        }
      })
    },

    filteredRows: function () {
      const search = this.search.toLowerCase()
      const status = this.statusFilter
      return this.rows.filter(function (row) {
        const matchesStatus = status === 'all' || row.status === status
        const haystack = [row.webRef, row.heading, row.type, row.location].join(' ').toLowerCase()
        return matchesStatus && (!search || haystack.indexOf(search) !== -1)
      })
    },

    agentOptions: function () {
      return this.agents.map(function (record) {
        const agent = getAgentData(record)
        return {
          value: record.ref_id,
          label: [agent.first_name, agent.last_name].filter(Boolean).join(' ') || agent.email_address || 'Unnamed agent'
        }
      }).filter(function (agent) { return agent.value })
    }
  },

  mounted: function () {
    this.load()
  },

  methods: {
    load: async function () {
      this.loading = true
      this.error = ''
      try {
        const results = await Promise.all([
          listManagedRecords('property'),
          listManagedRecords('agent')
        ])
        this.records = results[0]
        this.agents = results[1]
      } catch (error) {
        this.error = apiErrorMessage(error, 'Could not load properties.')
      } finally {
        this.loading = false
      }
    },

    formatPrice: function (value) {
      return Number(value || 0).toLocaleString('en-ZA', {
        style: 'currency',
        currency: 'ZAR',
        maximumFractionDigits: 0
      })
    },

    startCreate: function () {
      this.privateRefId = ''
      this.internalId = null
      this.form = emptyForm()
      this.form.webNumber = webRefNumber(nextWebRef(this.records))
      this.error = ''
      this.warning = ''
      this.message = ''
      this.activeTab = 'overview'
      this.view = 'editor'
      this.markFormClean()
      window.scrollTo(0, 0)
    },

    startEdit: function (row) {
      const record = row.record
      const payload = getRecordPayload(record)
      const listing = getPropertyListing(record)
      const images = Array.isArray(listing.listing_images) ? listing.listing_images : []
      const normalisedImages = images.map(function (image) {
        return typeof image === 'string' ? image : (image.url || image.image_url || '')
      }).filter(Boolean)
      const selectedFeaturedImage = payload.featured_image || listing.featured_image || normalisedImages[0] || ''
      if (selectedFeaturedImage && normalisedImages.indexOf(selectedFeaturedImage) === -1) {
        normalisedImages.unshift(selectedFeaturedImage)
      }
      const webRef = canonicalWebRef(listing.web_ref || listing.own_ref_number || record.ref_id)

      this.privateRefId = record.ref_id
      this.internalId = normaliseInternalId(record.id)
      this.form = {
        webNumber: webRefNumber(webRef),
        managerType: payload.manager_type || 'residential_sale',
        propertyType: listing.property_type || 'House',
        status: listing.status || 'Active',
        saleStatus: ['For Sale', 'Under Offer', 'Sold'].indexOf(listing.sale_status) !== -1 ? listing.sale_status : '',
        heading: listing.marketing_heading || '',
        description: listing.description || '',
        price: Number(listing.price || 0),
        priceTerm: normalisePriceTerm(listing.price_term),
        suburb: listing.loc_suburb || '',
        region: listing.loc_region || '',
        latitude: listing.map_x_position || '',
        longitude: listing.map_y_position || '',
        poa: listing.poa === true || listing.poa === 'true',
        onShow: listing.on_show === true || listing.on_show === 'true',
        bedrooms: Number(listing.bedrooms || 0),
        bathrooms: Number(listing.bathrooms || 0),
        lounges: Number(listing.lounges || 0),
        garages: Number(listing.garages || 0),
        parking: Number(listing.parking || 0),
        landSize: Number(listing.land_size || 0),
        landMeasurement: listing.measurement_type || 'm²',
        buildingSize: Number(listing.building_size || 0),
        buildingMeasurement: listing.building_measurement_type || listing.measurement_type || 'm²',
        images: normalisedImages,
        featuredImage: selectedFeaturedImage,
        videoId: listing.video_id || '',
        agent: listing.agent || '',
        agent2: listing.agent_2 || '',
        agent3: listing.agent_3 || '',
        featured: payload.is_featured_property === true,
        summary: payload.ai_description || '',
        seoTitle: payload.seo_title || '',
        seoDescription: payload.seo_description || ''
      }
      this.error = ''
      this.warning = this.internalId
        ? ''
        : 'This property does not have a valid database ID yet. Save it after correcting AUTO_INCREMENT before uploading images.'
      this.message = ''
      this.activeTab = 'overview'
      this.view = 'editor'
      this.markFormClean()
      window.scrollTo(0, 0)
    },

    returnToList: function () {
      if (this.hasUnsavedChanges()) {
        this.$emit('request-navigation')
        return
      }
      this.forceReturnToList()
    },

    forceReturnToList: function () {
      this.view = 'list'
      this.error = ''
      this.warning = ''
      window.scrollTo(0, 0)
    },

    formState: function () {
      return JSON.stringify(this.form)
    },

    markFormClean: function () {
      this.savedFormState = this.formState()
    },

    hasUnsavedChanges: function () {
      return this.isDirty
    },

    isEditingView: function () {
      return this.view === 'editor'
    },

    isBusy: function () {
      return this.saving || this.uploadingImages
    },

    saveForNavigation: async function () {
      return this.saveProperty()
    },

    handleManagerTypeChange: function () {
      if (this.propertyTypeOptions.indexOf(this.form.propertyType) === -1) {
        this.form.propertyType = this.propertyTypeOptions[0]
      }

      if (this.form.managerType.indexOf('rental') !== -1) {
        if (this.priceTermOptions.indexOf(this.form.priceTerm) === -1) {
          this.form.priceTerm = 'per month'
        }
      } else {
        this.form.priceTerm = ''
      }
    },

    webRefIsInUse: function (webRef) {
      return this.rows.some(function (row) {
        return row.webRef === webRef && row.privateRefId !== this.privateRefId
      }, this)
    },

    uploadSelectedImages: async function (event) {
      const files = event.target.files
      this.error = ''
      this.warning = ''

      if (!this.internalId) {
        this.error = 'Save the property before uploading images.'
        event.target.value = ''
        return
      }

      if (!files || !files.length) return

      this.uploadingImages = true
      this.uploadProgress = 0
      try {
        const urls = await uploadPropertyImages(this.internalId, files, function (progress) {
          this.uploadProgress = progress
        }.bind(this))

        urls.forEach(function (url) {
          if (this.form.images.indexOf(url) === -1) this.form.images.push(url)
        }, this)
        if (!this.form.featuredImage && this.form.images.length) {
          this.form.featuredImage = this.form.images[0]
        }
      } catch (error) {
        this.error = apiErrorMessage(error, 'Could not upload the property images.')
      } finally {
        this.uploadingImages = false
        this.uploadProgress = 0
        event.target.value = ''
      }
    },

    setFeaturedImage: function (image) {
      if (!image || this.form.images.indexOf(image) === -1) return
      this.form.featuredImage = image
    },

    removeImage: async function (index) {
      const image = this.form.images[index]
      if (!image || !window.confirm('Remove this image?')) return

      this.error = ''
      this.warning = ''
      this.uploadingImages = true
      try {
        if (this.internalId && image.indexOf('/uploads/properties/') !== -1) {
          await deletePropertyImage(this.internalId, image)
        }
        this.form.images.splice(index, 1)
        if (image === this.form.featuredImage) {
          this.form.featuredImage = this.form.images[0] || ''
        }
      } catch (error) {
        this.error = apiErrorMessage(error, 'Could not remove the property image.')
      } finally {
        this.uploadingImages = false
      }
    },

    buildPayload: function () {
      const webRef = makeWebRef(this.form.webNumber)
      const isRental = this.form.managerType.indexOf('rental') !== -1
      const isCommercial = this.form.managerType.indexOf('commercial') === 0
      const images = this.form.images.slice()
      const featuredImage = images.indexOf(this.form.featuredImage) !== -1
        ? this.form.featuredImage
        : (images[0] || '')

      return {
        schema_version: 1,
        manager_type: this.form.managerType,
        seo_title: this.form.seoTitle,
        seo_description: this.form.seoDescription,
        seo_keywords: '',
        ai_seo: '',
        ai_description: this.form.summary,
        ai_long_description: this.form.description,
        ai_location_description: '',
        ai_amenities_description: '',
        ai_activities_description: '',
        ai_schools_description: '',
        featured_image: featuredImage,
        template: 1,
        is_featured_property: this.form.featured,
        exclusion: '',
        tags: {
          buy: !isRental,
          rent: isRental,
          inland: true,
          coastal: false,
          countryside: true,
          city: false,
          estate: false,
          commercial: isCommercial
        },
        data: {
          listing: [{
            web_ref: webRef,
            status: this.form.status,
            sale_status: this.form.saleStatus,
            on_show: this.form.onShow,
            listing_type: isRental ? 'Rental' : 'Sale',
            property_type: this.form.propertyType,
            marketing_heading: this.form.heading,
            description: this.form.description,
            price: Number(this.form.price || 0),
            price_term: isRental ? this.form.priceTerm : '',
            poa: this.form.poa,
            loc_suburb: this.form.suburb,
            loc_region: this.form.region,
            map_x_position: this.form.latitude,
            map_y_position: this.form.longitude,
            land_size: Number(this.form.landSize || 0),
            measurement_type: this.form.landMeasurement,
            building_size: Number(this.form.buildingSize || 0),
            building_measurement_type: this.form.buildingMeasurement,
            lounges: Number(this.form.lounges || 0),
            bedrooms: Number(this.form.bedrooms || 0),
            bathrooms: Number(this.form.bathrooms || 0),
            garages: Number(this.form.garages || 0),
            parking: Number(this.form.parking || 0),
            video_id: normaliseYouTubeVideoId(this.form.videoId),
            agent: this.form.agent,
            agent_2: this.form.agent2,
            agent_3: this.form.agent3,
            listing_images: images
          }]
        }
      }
    },

    saveProperty: async function () {
      this.error = ''
      this.warning = ''
      if (this.uploadingImages) {
        this.error = 'Please wait for the image upload to finish.'
        return false
      }
      const webRef = makeWebRef(this.form.webNumber)
      if (!webRef) {
        this.error = 'Enter the numeric part of the PALE web reference.'
        return false
      }

      if (this.webRefIsInUse(webRef)) {
        this.warning = webRef + ' is already in use. Choose a different web reference before saving.'
        this.activeTab = 'overview'
        window.scrollTo(0, 0)
        return false
      }

      this.saving = true
      try {
        const payload = this.buildPayload()
        if (this.privateRefId) {
          await updateManagedRecord('property', this.privateRefId, payload)
        } else {
          await createManagedRecord('property', payload)
        }
        await this.load()

        const savedRow = this.rows.find(function (row) {
          return row.webRef === webRef
        })
        if (savedRow) {
          this.privateRefId = savedRow.privateRefId
          this.internalId = savedRow.internalId
        }

        this.markFormClean()
        this.message = webRef + ' saved.'
        this.statusFilter = this.form.status
        this.view = 'editor'
        return true
      } catch (error) {
        this.error = apiErrorMessage(error, 'Could not save the property.')
        return false
      } finally {
        this.saving = false
      }
    },

    removeProperty: async function () {
      if (!this.privateRefId || !window.confirm('Delete ' + this.webRefPreview + '?')) return
      this.saving = true
      this.error = ''
      try {
        await deleteManagedRecord('property', this.privateRefId)
        await this.load()
        this.markFormClean()
        this.message = this.webRefPreview + ' deleted.'
        this.view = 'list'
      } catch (error) {
        this.error = apiErrorMessage(error, 'Could not delete the property.')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
